/**
 * SSO → Dvoufaktor: politika (režim, metody, pravidla po tenantech/skupinách/
 * rolích, admin scope, uzly s vynucením), stav v Keycloaku (verze, podpora,
 * flow, override na klientech uzlu), uživatelé (příznak, faktory, výjimky).
 * Data: sso.mfa_status / mfa_policy_set / mfa_users / mfa_reconcile.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { KeyRound, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import { mcpCall } from '../../lib/mcp';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Select, Toggle } from '../../components/ui';

interface Rule {
  scope: { type: 'tenant' | 'group' | 'role'; id: string };
  mode: 'off' | 'optional' | 'required';
  note?: string;
}
interface Policy {
  default_mode: 'off' | 'optional' | 'required';
  methods: string[];
  rules: Rule[];
  require_for_admin_scopes: boolean;
  enforced_nodes: string[];
  version: number;
  updated_by?: string | null;
  updated_at?: string;
  reason?: string;
}
interface Status {
  ok?: boolean;
  error?: string;
  node_id: string;
  enforced_here: boolean;
  policy: Policy;
  keycloak: {
    instance?: string;
    realm?: string;
    mode?: string;
    version?: string | null;
    has_user_attribute_condition?: boolean;
    has_webauthn?: boolean;
    flow_id?: string | null;
    clients?: Array<{ client: string; override: string | null; mapper: boolean }>;
    error?: string;
  };
  users: { users: number; force: number; pending: number; exceptions: number; required_flag: number };
}
interface MfaUser {
  entity_id: string;
  label: string;
  email: string | null;
  flag: string;
  effective: string | null;
  kc_attr: string | null;
  configured: string[];
  setup_pending: boolean;
  exception: { until: string; reason: string } | null;
}

const MODES = ['off', 'optional', 'required'] as const;

export function SsoMfaPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const status = useQuery<Status>({ queryKey: ['sso-mfa-status'], queryFn: () => mcpCall('sso.mfa_status', {}) });
  const users = useQuery<{ ok?: boolean; users?: MfaUser[] }>({ queryKey: ['sso-mfa-users'], queryFn: () => mcpCall('sso.mfa_users', {}) });
  const [reason, setReason] = useState('');
  const [ruleType, setRuleType] = useState<'tenant' | 'group' | 'role'>('role');
  const [ruleId, setRuleId] = useState('');
  const [ruleMode, setRuleMode] = useState<'off' | 'optional' | 'required'>('required');
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['sso-mfa-status'] });
    qc.invalidateQueries({ queryKey: ['sso-mfa-users'] });
  };
  const set = useMutation({
    mutationFn: async (args: Record<string, unknown>) => {
      const r = await mcpCall<{ ok?: boolean; error?: string }>('sso.mfa_policy_set', { ...args, reason: reason || t('mfaUi.defaultReason') });
      if (r?.ok === false) throw new Error(r.error ?? t('mfaUi.error'));
    },
    onSuccess: () => {
      refresh();
      toast.success(t('mfaUi.saved'));
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const reconcile = useMutation({
    mutationFn: async () => {
      const r = await mcpCall<{ ok?: boolean; errors?: string[]; users?: { changed: number } }>('sso.mfa_reconcile', {});
      if (r?.errors?.length) throw new Error(r.errors.join('; '));
      return r;
    },
    onSuccess: (r) => {
      refresh();
      toast.success(`${t('mfaUi.reconciled')}: ${r?.users?.changed ?? 0}`);
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const userSet = useMutation({
    mutationFn: async (args: Record<string, unknown>) => {
      const r = await mcpCall<{ ok?: boolean; error?: string }>('sso.mfa_user_set', args);
      if (r?.ok === false) throw new Error(r.error ?? t('mfaUi.error'));
    },
    onSuccess: refresh,
    onError: (e) => toast.error(String((e as Error).message)),
  });

  const s = status.data;
  if (status.isLoading) return <p className='text-sm text-gray-500'>{t('mfaUi.loading')}</p>;
  if (!s || s.ok === false) return <p className='text-sm text-amber-400'>{s?.error ?? t('mfaUi.error')}</p>;
  const p = s.policy;
  const kc = s.keycloak;
  const modeLabel = (m: string) => t(`mfaUi.mode_${m}`);

  return (
    <div className='space-y-4'>
      <div className='grid gap-4 lg:grid-cols-2'>
        <Card>
          <CardHeader>
            <CardTitle>
              <span className='flex items-center gap-2'>
                <ShieldCheck size={14} /> {t('mfaUi.policyTitle')}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-3 text-sm'>
            <p className='text-[11px] text-gray-500'>{t('mfaUi.policyHint')}</p>
            <div className='flex flex-wrap items-center gap-3'>
              <span className='text-xs text-gray-400'>{t('mfaUi.defaultMode')}</span>
              <Select size='sm' value={p.default_mode} onChange={(e) => set.mutate({ default_mode: e.target.value })} options={MODES.map((m) => ({ value: m, label: modeLabel(m) }))} />
              <span className='text-xs text-gray-400'>{t('mfaUi.methods')}</span>
              {(['totp', 'webauthn'] as const).map((m) => (
                <label key={m} className='flex items-center gap-1 text-xs text-gray-300'>
                  <input
                    type='checkbox'
                    checked={p.methods.includes(m)}
                    onChange={(e) => set.mutate({ methods: e.target.checked ? [...new Set([...p.methods, m])] : p.methods.filter((x) => x !== m) })}
                  />
                  {m === 'totp' ? 'TOTP' : 'WebAuthn / passkey'}
                </label>
              ))}
            </div>
            <div className='flex flex-wrap items-center gap-3'>
              <Toggle value={p.require_for_admin_scopes} saving={set.isPending} onSave={(v) => set.mutateAsync({ require_for_admin_scopes: v })} />
              <span className='text-xs text-gray-400'>{t('mfaUi.adminScopes')}</span>
            </div>
            <div className='flex flex-wrap items-center gap-3'>
              <Toggle
                value={s.enforced_here}
                saving={set.isPending}
                onSave={(v) =>
                  set.mutateAsync({ enforced_nodes: v ? [...new Set([...p.enforced_nodes, s.node_id])] : p.enforced_nodes.filter((n) => n !== s.node_id), apply: true })}
              />
              <span className='text-xs text-gray-400'>{t('mfaUi.enforceHere')}</span>
              <span className='font-mono text-[10px] text-gray-600'>{s.node_id}</span>
            </div>
            <div>
              <div className='mb-1 text-xs text-gray-400'>{t('mfaUi.rules')}</div>
              <div className='divide-y divide-gray-800/60'>
                {p.rules.map((r) => (
                  <div key={`${r.scope.type}:${r.scope.id}`} className='flex items-center gap-2 py-1 text-xs'>
                    <Badge variant='secondary'>{r.scope.type}</Badge>
                    <span className='font-mono text-gray-300'>{r.scope.id}</span>
                    <Badge variant={r.mode === 'required' ? 'success' : r.mode === 'optional' ? 'info' : 'secondary'}>{modeLabel(r.mode)}</Badge>
                    <div className='flex-1' />
                    <button type='button' className='text-gray-500 hover:text-red-400' aria-label={t('mfaUi.remove')} onClick={() => set.mutate({ remove_rules: [{ type: r.scope.type, id: r.scope.id }] })}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <form
                className='flex flex-wrap gap-2 pt-1'
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!ruleId.trim()) return;
                  set.mutate({ rules: [{ scope: { type: ruleType, id: ruleId.trim() }, mode: ruleMode }] });
                  setRuleId('');
                }}
              >
                <Select size='sm' value={ruleType} onChange={(e) => setRuleType(e.target.value as 'role')} options={['tenant', 'group', 'role'].map((v) => ({ value: v, label: t(`mfaUi.scope_${v}`) }))} />
                <Input value={ruleId} onChange={(e) => setRuleId(e.target.value)} placeholder={t('mfaUi.rulePlaceholder')} className='font-mono text-xs' />
                <Select size='sm' value={ruleMode} onChange={(e) => setRuleMode(e.target.value as 'required')} options={MODES.map((m) => ({ value: m, label: modeLabel(m) }))} />
                <Button size='sm' type='submit' disabled={set.isPending || !ruleId.trim()}>{t('mfaUi.add')}</Button>
              </form>
            </div>
            <div className='flex flex-wrap items-center gap-2'>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('mfaUi.reasonPlaceholder')} className='text-xs' />
              <span className='text-[10px] text-gray-600'>
                v{p.version} · {p.updated_by ?? '—'} · {p.updated_at ? new Date(p.updated_at).toLocaleString('cs-CZ') : '—'}{p.reason ? ` · ${p.reason}` : ''}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <span className='flex items-center gap-2'>
                <KeyRound size={14} /> {t('mfaUi.kcTitle')}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-2 text-xs'>
            {kc.error && <p className='text-amber-400'>{kc.error}</p>}
            <div className='flex flex-wrap gap-2'>
              <Badge variant='secondary'>{kc.instance ?? '—'} / {kc.realm ?? '—'}</Badge>
              <Badge variant='secondary'>Keycloak {kc.version ?? '?'}</Badge>
              <Badge variant={kc.mode === 'write' ? 'success' : 'warning'}>{kc.mode ?? 'read'}</Badge>
              <Badge variant={kc.has_user_attribute_condition ? 'success' : 'error'}>{t('mfaUi.attrCondition')}</Badge>
              <Badge variant={kc.has_webauthn ? 'success' : 'secondary'}>WebAuthn</Badge>
              <Badge variant={kc.flow_id ? 'success' : 'secondary'}>{kc.flow_id ? t('mfaUi.flowReady') : t('mfaUi.flowMissing')}</Badge>
            </div>
            <div className='divide-y divide-gray-800/60'>
              {(kc.clients ?? []).map((c) => (
                <div key={c.client} className='flex items-center gap-2 py-1'>
                  <span className='font-mono text-gray-300'>{c.client}</span>
                  <div className='flex-1' />
                  <Badge variant={c.override ? 'success' : 'secondary'}>{c.override ? t('mfaUi.overrideOn') : t('mfaUi.overrideOff')}</Badge>
                  <Badge variant={c.mapper ? 'success' : 'secondary'}>{t('mfaUi.mapper')}</Badge>
                </div>
              ))}
            </div>
            <div className='flex flex-wrap gap-2'>
              <Badge variant='secondary'>{t('mfaUi.usersTotal')}: {s.users.users}</Badge>
              <Badge variant='info'>{t('mfaUi.usersForce')}: {s.users.force}</Badge>
              <Badge variant={s.users.pending ? 'warning' : 'secondary'}>{t('mfaUi.usersPending')}: {s.users.pending}</Badge>
              <Badge variant='secondary'>{t('mfaUi.usersExceptions')}: {s.users.exceptions}</Badge>
            </div>
            <Button size='sm' variant='secondary' disabled={reconcile.isPending} onClick={() => reconcile.mutate()}>
              <RefreshCw size={12} /> {t('mfaUi.reconcile')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('mfaUi.usersTitle')}</CardTitle>
        </CardHeader>
        <CardContent className='divide-y divide-gray-800/60'>
          {(users.data?.users ?? []).map((u) => (
            <div key={u.entity_id} className='flex flex-wrap items-center gap-2 py-1.5 text-xs'>
              <span className='text-gray-200'>{u.label}</span>
              {u.email && <span className='text-gray-500'>{u.email}</span>}
              <Select
                size='sm'
                value={u.flag ?? 'inherit'}
                onChange={(e) => userSet.mutate({ entity: u.entity_id, flag: e.target.value })}
                options={['inherit', 'optional', 'required'].map((f) => ({ value: f, label: t(`mfaUi.flag_${f}`) }))}
              />
              <Badge variant={u.effective === 'required' ? 'success' : u.effective === 'optional' ? 'info' : 'secondary'}>{modeLabel(u.effective ?? 'off')}</Badge>
              {u.configured?.length ? <Badge variant='success'>{u.configured.join(', ')}</Badge> : <Badge variant='secondary'>{t('mfaUi.noFactor')}</Badge>}
              {u.setup_pending && <Badge variant='warning'>{t('mfaUi.pending')}</Badge>}
              {u.exception && Date.parse(u.exception.until) > Date.now() && <Badge variant='warning'>{t('mfaUi.exception')} → {new Date(u.exception.until).toLocaleDateString('cs-CZ')}</Badge>}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
